import React, { useState } from 'react';
import styled from 'styled-components';
import ResponsiveModal from 'components/_UI/ResponsiveModal';
import { createReservation } from 'api/requests/reservations';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';
import colors from 'theme/colors';
import { useAuth } from 'context/auth/Auth';
import { useShowLoginModal } from 'context/general/ShowLoginModal';
import ReservationPicker from './reservation-picker/ReservationPicker';

const accent = colors.occasionalPurple;

export default function ReservationModal({ isOpen, onClose, establishmentSlug }) {
    const { toasts, toast } = useToast();
    const { isAuth } = useAuth();
    const { setShowLoginModal } = useShowLoginModal();
    const [personName, setPersonName] = useState('');
    const [phone, setPhone] = useState('');
    const [partySize, setPartySize] = useState(2);
    const [reservationDate, setReservationDate] = useState('');
    const [reservationTime, setReservationTime] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const resetForm = () => {
        setPersonName('');
        setPhone('');
        setPartySize(2);
        setReservationDate('');
        setReservationTime('');
        setNote('');
    };

    const handleClose = () => {
        if (submitting) return;
        onClose();
    };

    const isValid = personName.trim() && phone.trim() && reservationDate && reservationTime && Number(partySize) > 0;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValid || submitting) return;
        if (!isAuth) { setShowLoginModal(true); return; }

        try {
            setSubmitting(true);
            await createReservation(establishmentSlug, {
                person_name: personName.trim(),
                phone: phone.trim(),
                party_size: Number(partySize),
                reservation_date: reservationDate,
                reservation_time: reservationTime,
                note: note.trim() || null,
            });
            toast.success('Reserva solicitada! O estabelecimento vai confirmar em breve.');
            resetForm();
            onClose();
        } catch (error) {
            console.error('Error creating reservation', error);
            const message = error?.response?.status === 422
                ? (error?.response?.data?.message || 'Não foi possível reservar nesse horário.')
                : 'Erro ao solicitar a reserva. Tente novamente.';
            toast.error(message);
            // The slot may have just been taken — refresh availability so it greys out.
            if (error?.response?.status === 422) setRefreshKey(k => k + 1);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <ToastContainer toasts={toasts} />
            <ResponsiveModal isOpen={isOpen} onClose={handleClose} title='Reservar mesa' size='default'>
                <Form onSubmit={handleSubmit}>
                    <ReservationPicker
                        slug={establishmentSlug}
                        refreshKey={refreshKey}
                        value={{ partySize, date: reservationDate, time: reservationTime }}
                        onChange={({ partySize: size, date, time }) => {
                            setPartySize(size);
                            setReservationDate(date);
                            setReservationTime(time);
                        }}
                    />

                    <Field>
                        <Label htmlFor='reservation-name'>Nome</Label>
                        <Input
                            id='reservation-name'
                            value={personName}
                            onChange={e => setPersonName(e.target.value)}
                            placeholder='Seu nome'
                            required
                        />
                    </Field>

                    <Field>
                        <Label htmlFor='reservation-phone'>Telefone</Label>
                        <Input
                            id='reservation-phone'
                            type='tel'
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            placeholder='(00) 00000-0000'
                            required
                        />
                    </Field>

                    <Field>
                        <Label htmlFor='reservation-note'>Observação (opcional)</Label>
                        <Textarea
                            id='reservation-note'
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            placeholder='Ex: mesa perto da janela, aniversário, etc.'
                            rows={3}
                        />
                    </Field>

                    <SubmitButton type='submit' disabled={!isValid || submitting}>
                        {submitting ? 'Enviando...' : 'Solicitar reserva'}
                    </SubmitButton>
                </Form>
            </ResponsiveModal>
        </>
    );
}

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 4px 2px 8px;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Label = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const inputStyles = `
    width: 100%;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    padding: 11px 12px;
    font-size: 14px;
    font-family: inherit;
    color: ${colors.nightDark};
    background: #fff;
`;

const Input = styled.input`
    ${inputStyles}
    &:focus { outline: none; border-color: ${accent}; }
`;

const Textarea = styled.textarea`
    ${inputStyles}
    resize: vertical;
    &:focus { outline: none; border-color: ${accent}; }
`;

const SubmitButton = styled.button`
    margin-top: 4px;
    height: 48px;
    border-radius: 999px;
    border: none;
    background: ${accent};
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover:not(:disabled) { opacity: 0.92; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
